import { Link } from "react-router-dom";

export default function SavingsGoalHowLong() {
    return (
        <>
            <p>
                You have a savings target. Maybe it's a $20,000 emergency fund, a $50,000 down payment, or $10,000 for a car
                you want to pay cash for. The question "how long will it take?" has a precise answer if you know three numbers:
                what you have now, how much you'll save each month, and what interest rate you'll earn. Here's how to work
                through it.
            </p>

            <h2>The Basic Formula Without Interest</h2>
            <p>
                If your savings account earns little to no interest, the math is straightforward division:
            </p>
            <p>
                Time (months) = (Goal − Current Savings) ÷ Monthly Contribution
            </p>
            <p>
                You want to save $15,000 for a home down payment. You have $2,000 saved already. You can save $600/month.
            </p>
            <p>
                ($15,000 − $2,000) ÷ $600 = $13,000 ÷ $600 = 21.7 months — roughly 22 months from now.
            </p>
            <p>
                That's about 1 year and 10 months. If that timeline is too long, you have two levers: increase your monthly
                contribution or reduce your goal. To hit $15,000 in 18 months, you'd need to save $13,000 ÷ 18 = $722/month.
            </p>

            <h2>Adding Interest to the Calculation</h2>
            <p>
                When you earn interest on your savings, the timeline shortens — especially over longer horizons. A high-yield
                savings account or money market fund currently yields around 4–5% APY in early 2026. That adds up.
            </p>
            <p>
                On the $15,000 goal with $2,000 starting balance and $600/month at 4.5% APY, the math becomes more complex
                (it involves a future value of an annuity calculation), but the result is you reach your goal in roughly
                20 months instead of 22 — about 2 months faster. On longer goals the difference is more dramatic.
            </p>
            <p>
                Use the <Link to="/calculator/savings-goal">savings goal calculator</Link> to enter your specific numbers and
                see exactly how long it will take and what the interest earnings will be.
            </p>

            <h2>Bigger Goals Over Longer Timelines</h2>
            <p>
                When the timeline stretches to 5+ years, interest becomes a meaningful factor:
            </p>
            <p>
                Goal: $100,000 (down payment on a property)<br />
                Starting balance: $5,000<br />
                Monthly savings: $1,000<br />
                Interest rate: 4.5% APY
            </p>
            <p>
                Without interest: ($100,000 − $5,000) ÷ $1,000 = 95 months = 7 years 11 months<br />
                With 4.5% interest: approximately 81 months = 6 years 9 months
            </p>
            <p>
                That's 14 months faster just from earning interest on a high-yield account. The lesson: park your savings
                somewhere they earn something. Leaving $50,000 in a checking account at 0.01% while high-yield savings pay
                4.5% costs you real money.
            </p>

            <h2>How to Speed Up the Timeline</h2>
            <p>
                Two levers: save more each month, or earn more on what you've saved. Here's the impact of each on the $100,000
                goal above:
            </p>
            <p>
                Increase contribution by $200/month (to $1,200): reaches goal in roughly 71 months — 10 months faster than
                the base case with interest.<br />
                Increase interest rate from 4.5% to 5.5%: roughly 79 months — 2 months faster.
            </p>
            <p>
                More savings per month beats a higher interest rate, at least at realistic rate differences. That said, the
                easiest win is combining both: optimize your savings rate AND put the money somewhere it earns a decent yield.
            </p>

            <h2>Lump Sum Windfalls</h2>
            <p>
                One-time deposits — a tax refund, work bonus, inheritance, or sale proceeds — can shorten your timeline
                significantly. Adding a $3,000 lump sum to the example above reduces the remaining balance and the time
                proportionally.
            </p>
            <p>
                Practical application: If your tax refund averages $2,500/year, that's $208/month in effective savings even
                if you only receive it once per year. Factor windfalls into your plan so you're not leaving them out of your
                calculation.
            </p>

            <h2>When to Use Different Account Types</h2>
            <p>
                Match the account type to the goal:
            </p>
            <p>
                <strong>Emergency fund:</strong> High-yield savings account or money market account. Keep it liquid — no
                CDs or market investments.
            </p>
            <p>
                <strong>Short-term goal (under 3 years):</strong> High-yield savings or CD if the timeline is fixed. Avoid
                stock market investments where a bad year could delay your goal.
            </p>
            <p>
                <strong>Medium-term goal (3–7 years):</strong> A mix of CDs and conservative funds may be appropriate.
                Consider I-bonds for inflation protection.
            </p>
            <p>
                <strong>Long-term goal (7+ years):</strong> Index funds or a balanced portfolio, depending on your risk
                tolerance. The time horizon is long enough to weather market volatility.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                The formula is simple: know your starting point, your monthly contribution, your interest rate, and your goal.
                From those four numbers you can calculate exactly how long it takes and adjust accordingly. If the timeline
                isn't what you want, either change the monthly number or break the goal into phases. The{" "}
                <Link to="/calculator/savings-goal">savings goal calculator</Link> does the math instantly — try a few
                scenarios to see where you have room to move.
            </p>
        </>
    );
}